/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import {get} from '@ion/core/src/environment/environment';
import ApplicationFeatures, {
  GetPermittedRoutesParams,
  InitializeParams
} from '@ion/core/src/security/ApplicationFeatures';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  GenericObject,
  Link,
  Role,
  User
} from '@ion/core/src/types/entities';
import {
  ApplicationPage
} from '@ion/core/src/types/pages';

/**
 * Provides a reference to all pre-defined iOn Suite Application Features.
 * Each Feature is issued a unique Id which is used across all environments.
 *
 * The name of each feature is arbitrary and used for natural reference,
 * whereas the value of each feature represents the absolute Id of the
 * Feature.
 *
 * Each Feature id is defined, and assigned to a Feature within the CACTC
 * (iOn Suite) Application Entity's extendedAttributes value in each
 * environment.
 */
export const DASHBOARD_PAGE                       = '06409576-0c06-b170-ac8a-75bbc4b84c59';
export const DASHBOARD_PAGE_SAFETY                = '8584fe57-56d8-e446-c972-31a3fdbac40e';
export const DASHBOARD_PAGE_SAFETY_BASIC          = '08e6fef8-6a7d-4f3e-a094-ff4b1fcdf52b';
export const DASHBOARD_PAGE_UTILIZATION           = 'd2e30bdd-6e7d-8ecb-538f-d16b0afb21f4';
export const DASHBOARD_PAGE_DRIVER                = 'a7aff182-dae7-48c4-8376-44ac3d4d7164';
export const DASHBOARD_PAGE_DEVICES               = '83673521-aee4-2e00-d6f0-a4c98bf801e0';
export const TRACKING_PAGE                        = '713d20bb-3e04-6288-89f5-effab53c6660';
export const TRACKING_PAGE2                       = 'b6dff542-b94f-b15d-337c-5cdaa3a7960a';
export const TRACKING_CVS_FEATURES                = '0c7978e4-28f2-7740-3543-847695e1d882';
export const TRACKING_HVS_FEATURES                = '35c6c167-aaf5-c910-9d0c-4da441695795';
export const TRACKING_VEHICLE_UTILIZATION_DASHLET = '1e668d17-4e7e-c270-77fe-82d8bcfbfd76';
export const TRACKING_ENGINE_DIAGNOSTICS_DASHLET  = 'f29677d0-95bc-0d58-19ac-0e2c8f02d969';
export const TRACKING_SUPPLY_CHAIN                = '89c2026a-e4ea-52c7-4614-56704d909bee';
export const ALERTS_PAGE                          = 'cc1dd494-e6fc-8e08-86d4-45e130668165';
export const EVENTS                               = 'e5db63fd-df80-03c2-9009-e1bb054235ff';
export const EVENTS_GEOFENCES_PAGE                = 'b4a59780-bb79-5827-078d-ca8b30dbd4f1';
export const EVENTS_NOTIFICATIONS_PAGE            = 'cc1dd494-e6fc-8e08-86d4-45e130668165';
export const REPORTS_PAGE                         = '9a2efa71-8623-f092-5b71-df2540589cd1';
export const ANALYTICS_PAGE                       = '1e35a639-1f38-073f-42af-80eaaab4ea3b';
export const REPORT_MANAGER_PAGE                  = '9a50301e-1d82-afaa-6a8f-0864b9faf3c3';
export const REPORTS_REPORT                       = '';
export const MAINTENANCE                          = 'maintenancedashboard';
export const MAINTENANCE_PAGE                     = '396f6255-889f-9c2e-17b8-71aa3f905780';
export const MAINTENANCE_DASHBOARD                = '877b2e17-ad5a-92e9-e1b2-406a781b2f84';
export const MAINTENANCE_SERVICE_LOGS             = 'ddf1325f-844e-1cf0-6c51-b1f2f52b1f03';
export const VEHICLE_INSPECTIONS                  = '3ee28ec7-2561-6b91-676e-edffe31cc7a3';
export const VEHICLE_INSPECTIONS_SETUP            = '1fa4024b-12fe-1861-5cc5-fe04676bb614';
export const VEHICLE_INSPECTIONS_RESULTS          = '0a3301b8-fa71-ac30-2d04-82873a121d03';
export const TRIP                                 = '3ca44d43-fdf9-4a8e-ab38-9251834f5d9f';
export const TRIP_LIST                            = 'bfd639a3-fa1a-4f27-94d8-14034aa1b32a';
export const ADMIN                                = '75f17840-2933-e2f2-f3c0-ace6cdb0db5a';
export const ADMIN_ASSETS_PAGE                    = '68fb6d07-264b-6dd7-3021-14053540b8de';
export const EDIT_ASSET_STATUS                    = 'a39ee0ac-8376-e9d1-8254-69bdbacb67b1';
export const EDIT_DEVICE_ASSOCIATION              = '505899e8-a756-45fa-aaef-19841bcd585f';
export const ADMIN_ASSETS_DELETE                  = 'a78dd56e-b6dc-f7f7-b77a-d2588d5fe181';
export const ADMIN_DEVICES_PAGE                   = '78a2b2f8-d8ef-4407-9ccb-12a3ed3aa9e2';
export const ADMIN_OPERATORS_PAGE                 = '6ce381a4-5f72-8ea7-4d95-0ad64a2e8670';
export const EDIT_DRIVER_STATUS                   = '5ae9b8c0-cb55-cfc6-0618-6b32b77ed293';
export const ADMIN_ROUTES_PAGE                    = 'ee2f01d2-64c7-1bcc-4aaa-9784f06da900';
export const ADMIN_USERS_PAGE                     = '67288559-e942-e67a-60e9-ddc03f5d6864';
export const ADMIN_USERS_DELETE                   = '198b0489-ad35-c785-24ea-023351788cd9';
export const ADMIN_ROLES_PAGE                     = '807662d9-ce8d-ec6e-08f9-0482d168d11a';
export const ADMIN_GROUPS_PAGE                    = '2ca6d045-74a2-dab3-85f3-47fc07a14b57';
export const ADMIN_GROUPS_DELETE                  = '56eaaa00-a07f-8de1-4bc8-299054390633';
export const ADMIN_CURRENT_GROUPS_EDIT            = '05980971-7f59-f7b9-ad25-37b7bbd3f4da';
export const ADMIN_ALERTS_PAGE                    = 'd4662a72-346c-0477-b0cd-17b2b2aa214f';
export const ADMIN_MAINTENANCE_PAGE               = '16b8053b-a989-5f78-8bea-3c9f8ef98819';
export const ADMIN_SCHEDULES_PAGE                 = 'fd65a3fe-d143-8e07-0b4d-1cd509d1daff';
export const ADMIN_IO_PROFILES_PAGE               = 'c5b60b37-aa23-0bd0-a9f0-2ff10a79b769';
export const ADMIN_LANDMARK_GEOFENCE_PAGE         = '2ec66c10-4bee-9e7f-7450-dceb9195e342';
export const ADMIN_ASSET_DISABLE_FEATURES         = '16ba8ba7-dc2e-3e99-f27f-5eb138534d8c';
export const ADMIN_MAP_FEATURES                   = 'c15a7264-590f-e001-68b8-8ce69ae4e74f';
export const ADMIN_COMMAND_CENTER                 = 'd06a34ad-9c99-ce64-4974-5a8c5b8cd399';
export const ADMIN_DRIVER_SCORECARD               = 'b562bc46-5862-f507-77bd-9dfed3192ed2';
export const ADMIN_ASSETS_OVERRIDE_BUS_VALUE      = '65c0273b-30ae-871c-3c1d-10509ad95d7c';
export const REMOTE_VEHICLE_CONTROLS              = 'bb5698a7-9771-b2ad-1bc0-93c8bcbf1f8f';
export const EDIT_VEHICLE_CONTROL_STATUS          = 'c38b79bf-424f-67b9-0c0d-51d0fcf1f972';
export const VISION_BETA                          = 'e1bbc4d1-7dc1-4738-916f-7c44ff4cab9e';
export const VISION_HOME                          = 'd9f86b79-f9db-406b-85b3-a92a1324e72f';
export const VISION_LIVE                          = '7fdba53d-cabb-410b-8061-bb45f405d20e';
export const VISION_COACHING                      = '7fe56302-68c3-4d6d-ae34-8c94cfbc03be';
export const VISION_REPORTS                       = 'c0fd9973-0b8e-4cdb-b558-142e2a72b465';
export const VISION_HEALTH                        = '7b2f8b1e-ca2e-40a6-ae84-c3a91de50be4';
export const VISION_DRIVERS                       = 'd22a3a31-6584-4b57-b768-dc6cff77def6';
export const VISION_CONFIGURATIONS                = '57799eb9-9301-4868-9d26-f5c059f4b18f';
export const APP                                  = '21ce0965-5edf-4d6d-be47-e064cedf823e';
export const APP_NOTIFICATION                     = '18259259-1b87-413d-b3a6-37482ed45c7f';
export const APP_HELP                             = '381c23d4-fc5d-4cb3-abce-2dcf1e8baf61';
export const APP_GROUP_CHOOSER                    = '7f2a02e3-39d0-4b9b-9655-58e01d5b3007';
export const VISION_ASSETS                        = '6b5a5765-c5e5-4537-b940-7f510db2f907';
export const REPORT_SCHEDULER_BETA                = 'f527262f-5b4b-4256-9215-b1ef5e178739';

export const CRASHBOXX                            = 'add_ons_crashboxx';
export const ADD_ONS                              = '4b019edc-b1f3-f372-80d7-95f3a3536597';
export const ADD_ONS_CRASHBOXX                    = '863c84f9-02d4-aa8f-3199-7f1bc7a59bcd';
export const ADD_ONS_SCORE                        = 'bddce1b6-0399-4a35-3311-1f197780c9b1';
export const ADD_ONS_SENSE                        = '73567522-7bed-b48a-d82c-cf180801fe3f';
export const ADD_ONS_TOOLS                        = '00a14761-3509-7b0d-907a-8b984a7f2a68';
export const ADD_ONS_AUDIT                        = '90ee55c1-0253-3100-5186-4c0f2b9f7121';
export const VISION                               = 'add_ons_vision';
export const ADD_ONS_ION_VISION                   = '8faca041-9c46-d011-9dda-d0fa0d5c60ec';
export const ADD_ONS_ION_VISION_EVENTS            = 'f47349fa-0726-4452-9bbf-18e273031dc4';
export const ADD_ONS_SMART_TRAILER                = '8439ded7-c21e-7813-b10c-8a5d463f3218';
export const ELD                                  = 'add_ons_eld';
export const ADD_ONS_ELD                          = '21c215d0-8119-6f23-9c81-535b9888abb9';
export const REPORT_SCHEDULER_DRIVER              = '4523f6d4-c6af-493e-a799-fdeb23ecf3a7';
export const REPORT_SCHEDULER_DIRECTOR            = 'e4cde468-0d0b-4f7c-9db4-ab61d36accf1';
export const REPORT_SCHEDULER_FLEETMANAGER        = 'c9bd853b-6cb6-4674-b1d7-05a51ac22fc3';

// defines the features which restricted accounts have permission to access;
// if a feature is not present, a restricted account does NOT have permission
// to access it ... 
export const _restrictedAccountAllowedFeatures = [
  // Admin Feature/Pages
  ADMIN,
  ADMIN_ASSETS_PAGE,
  EDIT_DEVICE_ASSOCIATION,
  ADMIN_DEVICES_PAGE,
  ADMIN_ROLES_PAGE,
  ADMIN_USERS_PAGE,
  ADMIN_GROUPS_PAGE,
  ADMIN_GROUPS_DELETE,
  ADMIN_MAINTENANCE_PAGE,
  ADMIN_SCHEDULES_PAGE,
  ADMIN_IO_PROFILES_PAGE,
  ADD_ONS,
  ADD_ONS_AUDIT,

  // App Features
  APP,
  APP_NOTIFICATION,
  APP_HELP,
  APP_GROUP_CHOOSER
];

/**
 * The Features API provides the single, authoritative point of entry for initializing,
 * validating, and determining iOn Suite Feature  availability for authenticated Users
 * based on the Features available to the User's iOn Suite Roles.
 *
 * iOn Suite Features are defined within the features.json document of the cactc-ui-seed
 * project located at:
 *
 * @see http://bitbucket.pd.calampcorp.com:7990/projects/CACTC/repos/cactc-ui-seed/browse
 * @see http://bitbucket.pd.calampcorp.com:7990/repos/cactc-ui-seed/browse/src/json/features.json
 *
 *
 * ```typescript
 *
 *   import Features from '@/security';
 *
 *   [LOAD_ROLES_RESPONSE]: (draft, {roles: Role[]}) => {
 *     Features.initialize({
 *       user        : draft.user,
 *       allFeatures : draft.features,
 *       appKey      : get('appKey'),
 *       roles
 *     });
 *     ...
 *   }
 *
 * ```
 *
 * @class Features
 */
class Features extends ApplicationFeatures {
  /**
   * @private
   *
   * Indicates whether the current account is an iOn Suite restricted account.
   * (Initialized to null so that the initial setCurrentAccount() always returns
   * restrictedChanged = true)
   */
  _isCurrentAccountRestricted = null;

  /**
   * @private
   *
   * Indicates whether the specified feature (ID) is restricted for the current
   * account.
   */
  _isFeatureRestricted = (id: string): Boolean => {
    return _restrictedAccountAllowedFeatures.indexOf(id) < 0;
  };

  /**
   * Indicates whether the specified account (ID) is a restricted account.
   *
   * ```typescript
   *
   * import Features from '@/security/Features';
   *
   * const isRestricted = Features.isAccountRestricted(user.account.id);
   *
   * ```
   *
   */
  isAccountRestricted(id: number): boolean {
    const restrictedAccountIds = get('restrictedAccountIds');
    return Boolean(restrictedAccountIds?.indexOf(id) >= 0);
  }

  /**
   * Indicates whether the current account is a restricted account.
   *
   * ```typescript
   *
   * import Features from '@/security/Features';
   *
   * if (Features.isCurrentAccountRestricted()) {
   *   // hide a given component
   * }
   *
   * ```
   *
   */
  isCurrentAccountRestricted = (): boolean => {
    return Boolean(this._isCurrentAccountRestricted);
  };

  /**
   * Sets whether the current account is restricted. Intended to be called at
   * initialization/login and whenever else the global group/account is set.
   *
   * ```typescript
   *
   * import Features from '@/security/Features';
   *
   * const {isRestricted, restrictedChanged} = Features.setCurrentAccount(user.account);
   * if (restrictedChanged) {
   *   if (isRestricted) {
   *     // halt notifications
   *   } else {
   *     // start notifications
   *   }
   * }
   *
   * ```
   *
   */
  setCurrentAccount(account: Link): {isRestricted: boolean, restrictedChanged: boolean} {
    const wasRestricted              = this._isCurrentAccountRestricted;
    this._isCurrentAccountRestricted = this.isAccountRestricted(entityId(account));
    const isRestricted               = this._isCurrentAccountRestricted;
    const restrictedChanged          = wasRestricted !== isRestricted;
    return {isRestricted, restrictedChanged};
  }

  /**
   * Initializes iOn Suite Application Features and sets whether the current
   * account is restricted. See ApplicationFeatures.initialize() for more
   * information.
   */
  initialize({
    user, roles, allFeatures, appKey, enableLogging
  }: InitializeParams) {
    super.initialize({
      user,
      allFeatures,
      appKey,
      enableLogging,
      roles
    });
    this.setCurrentAccount(user.account);
  }

  /**
   * Determines if the current user can access all specified features,
   * taking into account whether the user's current account is restricted.
   * See ApplicationFeatures.hasFeature() for more information.
   */
  hasFeature(...ids): boolean {
    let permitted = super.hasFeature(...ids);
    if (permitted && this._isCurrentAccountRestricted) {
      permitted = ids.every(id => !this._isFeatureRestricted(id));
    }
    return permitted;
  }

  /**
   * Alias for the hasFeature() method.
   */
  has = (...ids): boolean => {
    return this.hasFeature(...ids);
  };

  /**
   * Determines if the current user can access any of the specified features,
   * taking into account whether the user's current account is restricted.
   * See ApplicationFeatures.hasAny() for more information.
   */
  hasAny = (...ids): boolean => {
    return Boolean(ids.find(id => this.hasFeature(id)));
  };

  /**
   * Returns the permitted features for the current user, taking into account
   * whether the user's current account is restricted.
   * See ApplicationFeatures.getPermittedFeatures() for more information.
   */
  getPermittedFeatures(): GenericObject {
    let permitted = super.getPermittedFeatures();
    if (this._isCurrentAccountRestricted) {
      permitted = permitted.filter(({id}) => !this._isFeatureRestricted(id));
    }
    return permitted;
  }

  /**
   * Returns the permitted pages for the current user, taking into account
   * whether the user's current account is restricted.
   * See ApplicationFeatures.getPermittedPages() for more information.
   */
  getPermittedPages({
    pages,
    permitted = [],
    isChild = false,
    parent = null
  }: GetPermittedRoutesParams) {
    return super.getPermittedPages({pages, permitted, isChild, parent});
  }

  /**
   * Returns whether the current user can access the specified pages, taking
   * into account whether the user's current account is restricted.
   * See ApplicationFeatures.canAccessPage() for more information.
   */
  canAccessPage(pages: ApplicationPage[], targetRoute: string): boolean {
    return super.canAccessPage(pages, targetRoute);
  }

  /**
   * Returns whether the current user can access the specified role, taking
   * into account whether the user's current account is restricted.
   * See ApplicationFeatures.canAccessRole() for more information.
   */
  canAccessRole(role: Role, verifyAssigned = false): boolean {
    return super.canAccessRole(role, verifyAssigned);
  }

  /**
   * Returns the permitted roles for the current user, taking into
   * account whether the user's current account is restricted.
   * See ApplicationFeatures.getPermittedRoles() for more information.
   */
  getPermittedRoles(roles: Role[]): Role[] {
    return super.getPermittedRoles(roles);
  }

  /**
   * The canChangePassword function returns True if the authenticated User
   * has Super Admin Privileges (ADD_ONS FEATURES).
   *
   * @method  canChangePassword
   * @returns {boolean} True if the authenticated User has Super Admin Privileges,
   *          otherwise, false.
   */
  canChangePassword(): boolean {
    return this.hasFeature(
      ADD_ONS,
      ADD_ONS_CRASHBOXX,
      ADD_ONS_SCORE,
      ADD_ONS_SENSE,
      ADD_ONS_TOOLS,
      ADD_ONS_AUDIT
    );
  }

  /**
   * Determines if the authenticated user has permissions for iOn Suite Application.
   *
   * @method  hasIOn
   * @returns {boolean} True if user has the iOn Application assigned (iOn) assigned,
   *          otherwise, false.
   */
  hasIOn() {
    return hasIOn(this._user);
  };

  /**
   * Determines if the authenticated user has permissions for iOn Vision Application.
   */
  hasIOnVision() {
    return hasIOnVision(this);
  }

  /**
   * Determines if the authenticated user has permissions for Crashboxx Application.
   */
  hasCrashboxx() {
    return hasCrashboxx(this);
  }

  /**
  * Determines if the authenticated user has permissions for Smart Trailer Feature.
  */
  hasSmartTrailer() {
    return hasSmartTrailer(this);
  }

  /**
  * Determines if the authenticated user has permissions for Supply Chain Devices on Tracking.
  */
  hasSCIAccess() {
    return hasSCIAccess(this);
  }

  /**
  * Determines if the authenticated user has permissions for Safety on Dashboard.
  */
  hasSafety() {
    return hasSafety(this);
  }

  /**
  * Determines if the authenticated user has permissions for Safety Basic on Dashboard.
  */
  hasSafetyBasic() {
    return hasSafetyBasic(this);
  }

  /**
  * Determines if the authenticated user has permissions for Fleet on Dashboard.
  */
  hasUtilization() {
    return hasUtilization(this);
  }

  /**
   * Determines if the authenticated user has permissions for Driver Dashboard.
   */
  hasDriverDashboardFeature() {
    return hasDriverDashboardFeature(this);
  }

  /**
    * Determines if the authenticated user has permissions for Devices on Dashboard.
    */
  hasDevices() {
    return hasDevices(this);
  }

  /**
   * Determines if the authenticated user has permissions for Report Scheduler BETA.
   */
  hasReportSchedulerBeta() {
    return hasReportSchedulerBeta(this);
  }

  /**
   * Determines if the authenticated user has permissions for Report Scheduler Director.
   */
  hasReportSchedulerDirector() {
    return hasReportSchedulerDirector(this);
  }

  /**
   * Determines if the authenticated user has permissions for Report Scheduler Fleet Manager.
   */
  hasReportSchedulerFleetManager() {
    return hasReportSchedulerFleetManager(this);
  }
}

const api = new Features();

/**
 * Determines if the authenticated user has application permissions
 * for iOn Suite.
 *
 * @method  hasIOn
 * @param   {object} user CTC User entity from which to determine if the
 *          user has the CACTC application assigned.
 * @returns {boolean} True if  user has the CACTC app, otherwise, false.
 */
export const hasIOn = (user: User, appKey?: string): boolean => {
  return Boolean(
    user?.applications.find(({href}) => href.indexOf(appKey || get('appKey')) > -1)
  );
};

/**
 * Determines if the authenticated user has permissions for iOn Suite Application.
 * The iOn Suite application shares the same appKey as iOn Prime, therefore this
 * method simply delegates to the hasIOn function.
 *
 * @method  hasIOnSuite
 * @param   {object} user CTC User entity from which to determine if the user has
 *          the iOn Suite Application assigned.
 * @returns {boolean} True if user has the iOn Suite Application assigned,
 *          otherwise, false.
 */
export const hasIOnSuite = (user: User, api: Features): boolean => {
  return hasIOn(user);
};

/**
 * Given an Features instance, determines if the user has the iOn Vision application
 * assigned.
 */
export const hasIOnVision = (instance: Features): boolean => {
  return Boolean(
    instance.hasFeature(ADD_ONS_ION_VISION)
  );
};

/**
 * Given an Features instance, determines if the user has the Crashboxx application
 * assigned.
 */
export const hasCrashboxx = (instance: Features): boolean => {
  return Boolean(
    instance.hasFeature(ADD_ONS_CRASHBOXX)
  );
};

/**
 * Given an Features instance, determines if the user has the Smart Trailer feature
 * assigned.
 */
export const hasSmartTrailer = (instance: Features): boolean => {
  return Boolean(
    instance.hasFeature(ADD_ONS_SMART_TRAILER)
  );
};

/**
 * Given an Features instance, determines if the user has the Supply Chain Devices on Tracking Access
 * assigned.
 */
export const hasSCIAccess = (instance: Features): boolean => {
  return Boolean(
    instance.hasFeature(TRACKING_SUPPLY_CHAIN)
  );
};

/**
 * Given an Features instance, determines if the user has the Safety on Dashboard Access
 * assigned.
 */
export const hasSafety = (instance: Features): boolean => {
  return Boolean(
    instance.hasFeature(DASHBOARD_PAGE_SAFETY)
  );
};

/**
 * Given an Features instance, determines if the user has the Safety Basic on Dashboard Access
 * assigned.
 */
export const hasSafetyBasic = (instance: Features): boolean => {
  return Boolean(
    instance.hasFeature(DASHBOARD_PAGE_SAFETY_BASIC)
  );
};

/**
 * Given an Features instance, determines if the user has the Fleet on Dashboard Access
 * assigned.
 */
export const hasUtilization = (instance: Features): boolean => {
  return Boolean(
    instance.hasFeature(DASHBOARD_PAGE_UTILIZATION)
  );
};

export const hasDriverDashboardFeature = (instance: Features): boolean => {
  return Boolean(
    instance.hasFeature(DASHBOARD_PAGE_DRIVER)
  );
};

/**
 * Given an Features instance, determines if the user has the Devices on Dashboard Access
 * assigned.
 */
export const hasDevices = (instance: Features): boolean => {
  return Boolean(
    instance.hasFeature(DASHBOARD_PAGE_DEVICES)
  );
};

/**
 * Given an Features instance, determines if the user has the permissions to edit
 * Driver status field in Admin Drivers editor dialog box.
 *
 * ```typescript
 *
 *   import {hasDriverStatusEditAccess} from 'ion-suite/src/security';
 *
 *   const isDriverStatusEditable = hasDriverStatusEditAccess();
 *
 * ```
 */
export const hasDriverStatusEditAccess = (instance = api): boolean => (
  instance.has(EDIT_DRIVER_STATUS)
);

/**
 * Given an Features instance, determines if the user has the permissions to edit Asset Status
 * field in Admin Assets editor dialog box.
 *
 * ```typescript
 *
 *   import {hasAssetStatusEditAccess} from 'ion-suite/src/security';
 *
 *   const isAssetStatusEditable = hasAssetStatusEditAccess();
 *
 * ```
 */
export const hasAssetStatusEditAccess = (instance = api): boolean => (
  instance.has(EDIT_ASSET_STATUS)
);

/**
 * Given an Features instance, determines if the user has the permissions to link
 * and de-link Devices from Assets, whether via the Admin Assets editor dialog box
 * or the Admin Assets bulk import.
 *
 * ```typescript
 *
 *   import {hasDeviceAssociationEditAccess} from 'ion-suite/src/security';
 *
 *   const isDeviceAssociationEditable = hasDeviceAssociationEditAccess();
 *
 * ```
 */
export const hasDeviceAssociationEditAccess = (instance = api): boolean => (
  instance.has(EDIT_DEVICE_ASSOCIATION)
);

/**
 * Given an Features instance, determines if the user has the permissions to edit current group.
 *
 * ```typescript
 *
 *   import {hasCurrentAccountEditAccess} from 'ion-suite/src/security';
 *
 *   const hasCurrentAccountEdit = hasCurrentAccountEditAccess();
 *
 * ```
 */
export const hasCurrentAccountEditAccess = (instance = api): boolean => (
  instance.has(ADMIN_CURRENT_GROUPS_EDIT)
);

/**
 * Determines if the user has access to Sisense-related pages/features.
 *
 * ```typescript
 *
 *   import {hasSisenseAccess} from '../../src/security/Features';
 *
 *   const hasAccess = hasSisenseAccess();
 *
 * ```
 * @param instance - The Features instance to check (optional, defaults to 'api').
 * @returns A boolean indicating whether the user has access to Sisense-related pages/features.
 */
export const hasSisenseAccess = (instance = api): boolean => (
  (instance.has(DASHBOARD_PAGE) && (
    instance.has(DASHBOARD_PAGE_UTILIZATION) ||
    instance.has(DASHBOARD_PAGE_DRIVER) ||
    instance.has(DASHBOARD_PAGE_SAFETY) ||
    instance.has(DASHBOARD_PAGE_SAFETY_BASIC)
  )) ||
  instance.has(ANALYTICS_PAGE) || instance.has(REPORT_MANAGER_PAGE) || instance.has(ADD_ONS_CRASHBOXX)
);

export const hasOverrideBusValueAccess = (instance = api): boolean => (
  instance.has(ADMIN_ASSETS_OVERRIDE_BUS_VALUE)
);

export const hasRemoteVehicleControlsAccess = (instance = api): boolean => (
  instance.has(REMOTE_VEHICLE_CONTROLS)
);

export const hasEditVehicleControlStatusAccess = (instance = api): boolean => (
  instance.has(EDIT_VEHICLE_CONTROL_STATUS)
);

export const hasLandmarkGeofenceAccess = (instance = api): boolean => (
  instance.has(ADMIN_LANDMARK_GEOFENCE_PAGE)
);

export const hasHistoryAccess = (instance = api): boolean => (
  instance.has(TRACKING_HVS_FEATURES)
);

export const hasMaintanenceAccess = (instance = api): boolean => (
  instance.has(MAINTENANCE_PAGE)
);

export const hasAnalyticsPageAccess = (instance = api): boolean => (
  instance.has(ANALYTICS_PAGE)
);

export const hasTrackingPageAccess = (instance = api): boolean => (
  instance.has(TRACKING_PAGE)
);

export const hasAppNotificationAccess = (instance = api): boolean => (
  instance.has(APP_NOTIFICATION)
);

export const hasAppHelpAccess = (instance = api): boolean => (
  instance.has(APP_HELP)
);

export const hasAppGroupChooserAccess = (instance = api): boolean => (
  instance.has(APP_GROUP_CHOOSER)
);

export const hasReportSchedulerBeta = (instance = api): boolean => (
  instance.has(REPORT_SCHEDULER_BETA)
);

export const hasReportSchedulerDirector = (instance = api): boolean => (
  instance.has(REPORT_SCHEDULER_DIRECTOR)
);

export const hasReportSchedulerFleetManager = (instance = api): boolean => (
  instance.has(REPORT_SCHEDULER_FLEETMANAGER)
);

export default api;
